/**
 * Ground colours per biome, and their blend for one point.
 *
 * The terrain shader no longer decides what colour the ground is: every
 * terrain vertex carries its own blended palette, computed here in the worker
 * from the climate at that vertex. That keeps the classification in one place
 * — this module and `biome.ts` — rather than half in TypeScript for the trees
 * and half in GLSL for the ground, where the two would drift.
 *
 * Colours are authored in sRGB hex and stored as linear-light bytes, the space
 * the shader works in.
 */

import type { BiomeWeights, Biome } from './biome.js';
import { BIOMES } from './biome.js';

interface GroundTones {
  /** The dominant ground cover. */
  ground: number;
  /** The patch colour it is broken up with — meadow, dry grass, dune shadow. */
  patch: number;
  /** Exposed rock on steep slopes. */
  rock: number;
  /** 0..1, how strongly the patch colour runs in wind-blown dune bands. */
  dunes: number;
}

export const GROUND: Record<Biome, GroundTones> = {
  glacier: { ground: 0xeef3f8, patch: 0xdce6f1, rock: 0x9aa3ae, dunes: 0 },
  tundra: { ground: 0x8e9a62, patch: 0xa99f71, rock: 0x86898a, dunes: 0 },
  boreal: { ground: 0x4c8537, patch: 0x66923f, rock: 0x8c8f91, dunes: 0 },
  temperate: { ground: 0x4aa032, patch: 0x74b03f, rock: 0xaaa69b, dunes: 0 },
  mediterranean: { ground: 0xa4a359, patch: 0xc6b274, rock: 0xcdbfa1, dunes: 0 },
  steppe: { ground: 0xaea85e, patch: 0xc8b87b, rock: 0xa6927a, dunes: 0.1 },
  desert: { ground: 0xeac98f, patch: 0xd4a568, rock: 0xc2764a, dunes: 1 },
  savanna: { ground: 0xc8ae59, patch: 0x9ea04c, rock: 0xb07a56, dunes: 0 },
  rainforest: { ground: 0x3a7d31, patch: 0x4c8c36, rock: 0x6e6a58, dunes: 0 },
};

function toLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** Linear RGB per biome, precomputed. */
const LINEAR = Object.fromEntries(
  BIOMES.map((b) => {
    const t = GROUND[b];
    const rgb = (hex: number) => [toLinear((hex >> 16) & 255), toLinear((hex >> 8) & 255), toLinear(hex & 255)];
    return [b, { ground: rgb(t.ground), patch: rgb(t.patch), rock: rgb(t.rock), dunes: t.dunes }];
  }),
) as Record<Biome, { ground: number[]; patch: number[]; rock: number[]; dunes: number }>;

/**
 * Write one vertex's blended ground palette into three RGBA8 slots:
 * `ground` rgb + snow, `patch` rgb + dunes, `rock` rgb + unused.
 */
export function writeGroundPalette(
  weights: BiomeWeights,
  snow: number,
  ground: Uint8Array,
  patch: Uint8Array,
  rock: Uint8Array,
  offset: number,
  /** Grass vigour, 0 dormant .. 1 lush — winter turns the lawn to straw. */
  greenness = 1,
): void {
  let gr = 0, gg = 0, gb = 0, pr = 0, pg = 0, pb = 0, rr = 0, rg = 0, rb = 0, dunes = 0;
  for (const b of BIOMES) {
    const w = weights[b];
    if (w <= 0) continue;
    const t = LINEAR[b];
    gr += t.ground[0] * w; gg += t.ground[1] * w; gb += t.ground[2] * w;
    pr += t.patch[0] * w; pg += t.patch[1] * w; pb += t.patch[2] * w;
    rr += t.rock[0] * w; rg += t.rock[1] * w; rb += t.rock[2] * w;
    dunes += t.dunes * w;
  }
  // Dormant grass: toward a pale straw olive, never all the way — a Swedish
  // lawn in March is tired, not dead.
  const dormant = (1 - greenness) * 0.65;
  if (dormant > 0) {
    gr += (0.26 - gr) * dormant; gg += (0.22 - gg) * dormant; gb += (0.09 - gb) * dormant;
    pr += (0.3 - pr) * dormant; pg += (0.24 - pg) * dormant; pb += (0.1 - pb) * dormant;
  }
  const byte = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255)));
  ground[offset] = byte(gr); ground[offset + 1] = byte(gg); ground[offset + 2] = byte(gb);
  ground[offset + 3] = byte(snow);
  patch[offset] = byte(pr); patch[offset + 1] = byte(pg); patch[offset + 2] = byte(pb);
  patch[offset + 3] = byte(dunes);
  rock[offset] = byte(rr); rock[offset + 1] = byte(rg); rock[offset + 2] = byte(rb);
  rock[offset + 3] = 255;
}

/** The blended sRGB hex of one ground tone, for props that match the ground. */
export function blendedTone(weights: BiomeWeights, tone: 'ground' | 'patch' | 'rock'): number {
  let r = 0, g = 0, b = 0;
  for (const biome of BIOMES) {
    const w = weights[biome];
    const hex = GROUND[biome][tone];
    r += ((hex >> 16) & 255) * w;
    g += ((hex >> 8) & 255) * w;
    b += (hex & 255) * w;
  }
  return (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b);
}
